// Canonical Supreme Wet Wall Panel imagery (owned photography, CDN hosted).
// Every wet wall image across the site must come from this module.

import blackPanel from "@/assets/products/black-sensation-supreme-wet-wall-panel.jpg.asset.json";
import blackInstall1 from "@/assets/products/black-sensation-supreme-wet-wall-panel-installation-1.jpg.asset.json";
import blackInstall2 from "@/assets/products/black-sensation-supreme-wet-wall-panel-installation-2.jpg.asset.json";
import blackInstall3 from "@/assets/products/black-sensation-supreme-wet-wall-panel-installation-3.jpg.asset.json";
import blackInstall4 from "@/assets/products/black-sensation-supreme-wet-wall-panel-installation-4.jpg.asset.json";

import whitePanel from "@/assets/products/white-sparkle-supreme-wet-wall-panel.jpg.asset.json";
import whiteInstall1 from "@/assets/products/white-sparkle-supreme-wet-wall-panel-installation-1.jpg.asset.json";
import whiteInstall2 from "@/assets/products/white-sparkle-supreme-wet-wall-panel-installation-2.jpg.asset.json";
import whiteInstall3 from "@/assets/products/white-sparkle-supreme-wet-wall-panel-installation-3.jpg.asset.json";

import retroPanel from "@/assets/products/retro-grey-supreme-wet-wall-panel.jpg.asset.json";
import retroInstall1 from "@/assets/products/retro-grey-supreme-wet-wall-panel-installation-1.jpg.asset.json";
import retroInstall2 from "@/assets/products/retro-grey-supreme-wet-wall-panel-installation-2.jpg.asset.json";
import retroInstall3 from "@/assets/products/retro-grey-supreme-wet-wall-panel-installation-3.jpg.asset.json";

import greyPanel from "@/assets/products/grey-marble-supreme-wet-wall-panel.jpg.asset.json";
import greyInstall1 from "@/assets/products/grey-marble-supreme-wet-wall-panel-installation-1.jpg.asset.json";
import greyInstall2 from "@/assets/products/grey-marble-supreme-wet-wall-panel-installation-2.jpg.asset.json";
import greyInstall3 from "@/assets/products/grey-marble-supreme-wet-wall-panel-installation-3.jpg.asset.json";

import carraraPanel from "@/assets/products/carrara-marble-supreme-wet-wall-panel.jpg.asset.json";
import carraraInstall1 from "@/assets/products/carrara-marble-supreme-wet-wall-panel-installation-1.jpg.asset.json";
import carraraInstall2 from "@/assets/products/carrara-marble-supreme-wet-wall-panel-installation-2.jpg.asset.json";
import carraraInstall3 from "@/assets/products/carrara-marble-supreme-wet-wall-panel-installation-3.jpg.asset.json";

import stonePanel from "@/assets/products/stone-beige-supreme-wet-wall-panel.jpg.asset.json";
import stoneInstall1 from "@/assets/products/stone-beige-supreme-wet-wall-panel-installation-1.jpg.asset.json";
import stoneInstall2 from "@/assets/products/stone-beige-supreme-wet-wall-panel-installation-2.jpg.asset.json";
import stoneInstall3 from "@/assets/products/stone-beige-supreme-wet-wall-panel-installation-3.jpg.asset.json";

export interface WetWallAssetSet {
  id: string;
  colour: string;
  productName: string;
  /** Primary product-card / main product image */
  primary: string;
  /** Installation photography, in display order */
  installations: string[];
  /** primary + installations */
  images: string[];
  /** Descriptive alt text aligned 1:1 with `images` */
  alts: string[];
}

function build(
  id: string,
  colour: string,
  primary: { url: string },
  installations: { url: string }[],
): WetWallAssetSet {
  const productName = `${colour} Supreme Wet Wall Panel`;
  const images = [primary.url, ...installations.map((a) => a.url)];
  const alts = [
    `${productName} from Supreme Turf`,
    ...installations.map(
      (_, i) => `${productName} installed in a bathroom – Supreme Turf installation photo ${i + 1}`,
    ),
  ];
  return {
    id,
    colour,
    productName,
    primary: primary.url,
    installations: installations.map((a) => a.url),
    images,
    alts,
  };
}

export const WET_WALL_ASSETS: Record<string, WetWallAssetSet> = {
  "black-sensation": build("black-sensation", "Black Sensation", blackPanel, [
    blackInstall1,
    blackInstall2,
    blackInstall3,
    blackInstall4,
  ]),
  "white-sparkle": build("white-sparkle", "White Sparkle", whitePanel, [
    whiteInstall1,
    whiteInstall2,
    whiteInstall3,
  ]),
  "retro-grey": build("retro-grey", "Retro Grey", retroPanel, [
    retroInstall1,
    retroInstall2,
    retroInstall3,
  ]),
  "grey-marble": build("grey-marble", "Grey Marble", greyPanel, [
    greyInstall1,
    greyInstall2,
    greyInstall3,
  ]),
  "carrara-marble": build("carrara-marble", "Carrara Marble", carraraPanel, [
    carraraInstall1,
    carraraInstall2,
    carraraInstall3,
  ]),
  "stone-beige": build("stone-beige", "Stone Beige", stonePanel, [
    stoneInstall1,
    stoneInstall2,
    stoneInstall3,
  ]),
};

export const WET_WALL_ASSET_LIST = Object.values(WET_WALL_ASSETS);

/** Resolve an asset set from a free-form product title. */
export function resolveWetWallAssets(title: string): WetWallAssetSet | null {
  const t = title.toLowerCase();
  return (
    WET_WALL_ASSET_LIST.find((a) => t.includes(a.colour.toLowerCase())) ??
    WET_WALL_ASSET_LIST.find((a) => t.includes(a.id.replace("-", " "))) ??
    null
  );
}
